/**
 * R7.2.2F2 — canonical Customer actionable product contract for recommendation cards.
 * Recommendation ranking is NOT changed. This resolves identity → open PDP / add to cart.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { getCustomerSalePrice } from "./money-v2.js";

export type RecommendationActionSurface =
  | "PDP_CROSS_SELL"
  | "PDP_MAY_LIKE"
  | "PDP_RECENTLY_VIEWED"
  | "HOME_PROMOTIONS"
  | "HOME_FOR_YOU"
  | "SEARCH_SIMILAR"
  | "VISUAL_SIMILAR"
  | "CART_COMPLEMENT"
  | "CHECKOUT_CROSS_SELL"
  | "PRECHECKOUT_CROSS_SELL";

/** Canonical actionable product — CatalogV2Product plus stable Customer route/cart fields. */
export type CustomerActionableProduct = CatalogV2Product & {
  customer_product_ref: string;
  payable_price_uah: number;
  customer_visible: boolean;
  route: { name: "product"; productId: string };
  cart_identity: string;
};

export type RecommendationCandidateLike = {
  id?: string | null;
  product_id?: string | null;
  canonical_product_id?: string | null;
  customer_product_ref?: string | null;
  sku?: string | null;
  prom_product_id?: string | null;
  title?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  price_uah?: number | null;
  priceUah?: number | null;
  old_price_uah?: number | null;
  availability?: string | null;
  stock_state?: string | null;
  brand?: string | null;
};

export type ActionableResolveOutcome =
  | { ok: true; product: CustomerActionableProduct; source: "catalog" | "shell" | "seed" | "promo_shell" | "onsite_shell" }
  | { ok: false; reason: "unresolved" | "not_customer_visible" | "missing_id" };

export function toCustomerActionableProduct(product: CatalogV2Product): CustomerActionableProduct | null {
  const ref = String(product.id ?? "").trim();
  if (!ref) return null;
  const payable = getCustomerSalePrice(product.priceUah);
  const customer_visible =
    product.isSellable !== false &&
    (product.isPriced === true || (typeof product.priceUah === "number" && product.priceUah > 0));
  if (!customer_visible && product.stockStatus === "out_of_stock") return null;
  return {
    ...product,
    customer_product_ref: ref,
    payable_price_uah: payable,
    customer_visible: customer_visible || Boolean(product.title),
    route: { name: "product", productId: ref },
    cart_identity: ref,
  };
}

/** Register products into the session shell map (Cart known-good pattern). */
export function registerActionableProducts(
  shellById: Map<string, CatalogV2Product>,
  products: Iterable<CatalogV2Product>,
): void {
  for (const p of products) {
    const id = String(p.id ?? "").trim();
    if (!id) continue;
    shellById.set(id, p);
    const prom = p.promProductId != null ? String(p.promProductId).trim() : "";
    if (prom && prom !== id) shellById.set(prom, p);
  }
}

export function resolveActionableProduct(input: {
  candidate?: RecommendationCandidateLike | null;
  product?: CatalogV2Product | null;
  productId?: string | null;
  catalogById: Map<string, CatalogV2Product>;
  shellById: Map<string, CatalogV2Product>;
}): ActionableResolveOutcome {
  if (input.product) {
    const actionable = toCustomerActionableProduct(input.product);
    if (!actionable) return { ok: false, reason: "not_customer_visible" };
    registerActionableProducts(input.shellById, [actionable]);
    return { ok: true, product: actionable, source: "seed" };
  }

  const rawId = String(
    input.productId ??
      input.candidate?.customer_product_ref ??
      input.candidate?.canonical_product_id ??
      input.candidate?.product_id ??
      input.candidate?.id ??
      "",
  ).trim();
  if (!rawId) return { ok: false, reason: "missing_id" };

  const fromCatalog = input.catalogById.get(rawId);
  if (fromCatalog) {
    const actionable = toCustomerActionableProduct(fromCatalog);
    if (!actionable) return { ok: false, reason: "not_customer_visible" };
    registerActionableProducts(input.shellById, [actionable]);
    return { ok: true, product: actionable, source: "catalog" };
  }

  const fromShell = input.shellById.get(rawId);
  if (fromShell) {
    const actionable = toCustomerActionableProduct(fromShell);
    if (!actionable) return { ok: false, reason: "not_customer_visible" };
    return { ok: true, product: actionable, source: "shell" };
  }

  if (input.candidate) {
    const shell = shellFromRecommendationCandidate(input.candidate, rawId);
    if (shell) {
      const actionable = toCustomerActionableProduct(shell);
      if (!actionable) return { ok: false, reason: "not_customer_visible" };
      registerActionableProducts(input.shellById, [actionable]);
      return { ok: true, product: actionable, source: "promo_shell" };
    }
  }

  return { ok: false, reason: "unresolved" };
}

/** Build a Customer shell from Home/Promotions JSON (never treat promotion id as separate identity). */
export function shellFromRecommendationCandidate(
  item: RecommendationCandidateLike,
  forcedId?: string,
): CatalogV2Product | null {
  const id = String(forcedId ?? item.customer_product_ref ?? item.canonical_product_id ?? item.product_id ?? item.id ?? "").trim();
  if (!id) return null;
  const priceRaw = item.priceUah ?? item.price_uah;
  const price = typeof priceRaw === "number" && Number.isFinite(priceRaw) ? getCustomerSalePrice(priceRaw) : null;
  const title = String(item.title ?? "").trim() || id;
  const availability = String(item.availability ?? item.stock_state ?? "in_stock").toLowerCase();
  const outOfStock = availability === "out_of_stock" || availability === "unavailable";
  const available = !outOfStock && price != null && price > 0;
  const looksProm = /^\d{6,}$/.test(id);
  return {
    id,
    sku: id,
    title,
    titleRu: null,
    categoryKeys: [],
    categoryName: "",
    imageUrl: item.imageUrl ?? item.image ?? null,
    productUrl: null,
    priceUah: price,
    displayPrice: price != null && price > 0 ? `${Math.trunc(price)} грн` : "",
    isPriced: price != null && price > 0,
    stockStatus: outOfStock ? "out_of_stock" : "in_stock",
    isSellable: available,
    sellabilityReason: available ? "available" : outOfStock ? "out_of_stock" : "unavailable",
    brand: item.brand ?? null,
    country: null,
    weight: null,
    regularPriceUah:
      typeof item.old_price_uah === "number" ? getCustomerSalePrice(item.old_price_uah) : null,
    salePriceUah: price,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: null,
    sourceRevision: null,
    search: title.toLowerCase(),
    promProductId: looksProm ? id : item.prom_product_id != null ? String(item.prom_product_id) : null,
    merchantSku: looksProm ? null : id,
    merchantArticle: null,
    barcodeGtin: null,
    barcodeVerification: "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: "recommendation_actionable_f2",
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: null,
    catalogArrivedAt: null,
    sourceTitle: title,
    customerTitle: title,
    titleAliases: [],
    originalSkuVerified: false,
    nutrition: null,
    maxOrderQty: null,
  };
}

/** Dedupe by canonical id (not title). Drop self on PDP. */
export function dedupeActionableCandidates(
  items: CatalogV2Product[],
  opts?: { excludeProductId?: string | null },
): CatalogV2Product[] {
  const seen = new Set<string>();
  const exclude = opts?.excludeProductId ? String(opts.excludeProductId).trim() : "";
  const out: CatalogV2Product[] = [];
  for (const p of items) {
    const id = String(p.id ?? "").trim();
    const prom = p.promProductId != null ? String(p.promProductId).trim() : "";
    if (!id) continue;
    if (seen.has(id) || (prom && seen.has(prom))) continue;
    if (exclude && (id === exclude || prom === exclude || p.promProductId === exclude)) continue;
    seen.add(id);
    if (prom) seen.add(prom);
    out.push(p);
  }
  return out;
}

export function filterRenderableActionable(products: CatalogV2Product[]): CatalogV2Product[] {
  return products.filter((p) => {
    const a = toCustomerActionableProduct(p);
    return a != null && a.customer_visible;
  });
}
